// src/pages/Login.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { postData } from '../services/Fetchnodeservices';

const Login = () => {
  const navigate = useNavigate();
  const { login } = usePermissions();
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Validate email or phone
  const isValidEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const isValidPhone = (value) => {
    return /^[0-9]{10}$/.test(value);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation: Check if email/phone is provided
    if (!emailOrPhone.trim()) {
      setError('Please enter email or phone number');
      setLoading(false);
      return;
    }

    // Validation: Check if password is provided
    if (!password.trim()) {
      setError('Please enter your password');
      setLoading(false);
      return;
    }

    // Validate format
    const isEmail = isValidEmail(emailOrPhone);
    const isPhone = isValidPhone(emailOrPhone);

    if (!isEmail && !isPhone) {
      setError('Please enter a valid email or 10-digit phone number');
      setLoading(false);
      return;
    }

    try {
      // Determine if input is email or phone
      const payload = { 
        [isEmail ? 'email' : 'phone']: emailOrPhone, 
        password 
      };

      // console.log("📤 Login Request:", payload);

      const response = await postData("login/AdminloginbyRole", payload);
         
      console.log("📥 Login Response:", response);

      if (response && response.status === true) {
        const responseData = response.data || response.user || {};
        
        const admin = responseData.admin || responseData;
        const roleData = responseData.role || admin?.role || null;
        const permissionsData = roleData?.permissions || responseData.permissions || [];
        
        const completeUserData = {
          admin: admin,
          role: roleData,
          permissions: permissionsData,
          ...responseData
        };
        
        localStorage.setItem('userData', JSON.stringify(completeUserData));
        localStorage.setItem('userPermissions', JSON.stringify(permissionsData));
        if (responseData.token) {
          localStorage.setItem('Token', responseData.token);
        }
        
        const loginSuccess = await login(completeUserData);
        
        if (loginSuccess) {
          navigate('/dashboard');
        } else {
          setError('Login failed. Please try again.');
        }
      } else {
        setError(response?.message || 'Invalid credentials');
      }
    } catch (err) {
      console.error("❌ Login Error:", err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f0f4ff, #e8edff)',
      padding: '20px',
    }}>
      <div style={{
        background: '#fff',
        padding: '40px',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
        maxWidth: '400px',
        width: '100%',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '60px',
            height: '60px',
            background: 'linear-gradient(135deg, #6c63ff, #a78bfa)',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: '28px',
            fontWeight: 700,
            color: '#fff',
          }}>
            A
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
            Welcome Back
          </h2>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>
            Sign in to your account
          </p>
        </div>

        {error && (
          <div style={{
            padding: '12px',
            background: '#fee2e2',
            color: '#991b1b',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            textAlign: 'center',
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} noValidate>
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: '#0f172a',
              marginBottom: '6px',
            }}>
              Email or Phone <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              value={emailOrPhone}
              onChange={(e) => {
                setEmailOrPhone(e.target.value);
                if (error) setError('');
              }}
              placeholder="admin@example.com or 9876543210"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: `1px solid ${error && !emailOrPhone ? '#ef4444' : '#e2e8f5'}`,
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#6c63ff'}
              onBlur={(e) => {
                if (!error) e.target.style.borderColor = '#e2e8f5';
              }}
              required
            />
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
              Enter email or 10-digit phone number
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: '#0f172a',
              marginBottom: '6px',
            }}>
              Password <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError('');
                }}
                placeholder="Enter your password"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  paddingRight: '44px',
                  border: `1px solid ${error && !password ? '#ef4444' : '#e2e8f5'}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = '#6c63ff'}
                onBlur={(e) => {
                  if (!error) e.target.style.borderColor = '#e2e8f5';
                }}
                required
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#94a3b8',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              background: 'linear-gradient(135deg, #6c63ff, #a78bfa)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: loading ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (!loading) e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={{
          marginTop: '20px',
          textAlign: 'center',
          fontSize: '13px',
          color: '#94a3b8',
        }}>
          Demo Credentials: admin@example.com / password
        </div>
      </div>
    </div>
  );
};

export default Login;
